import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from 'rxjs';

import { User } from "@model/User";
import { Institution } from "@model/admin/institution";
import { Paginate } from "@model/admin/paginate";
import { UserAccess } from "@model/admin/user-access";
import { UserExtract } from "@model/admin/user-extract";
import { PixRequest } from "@model/admin/pix-request";

import { environment } from "@env/environment";
import { tap } from "rxjs/operators";

export interface Guest {
  email: string;
}

@Injectable({
  providedIn: "root",
})
export class UsersService {
  public PROFILE_CODS = [
    { id: "student", description: "Estudante" },
    { id: "teacher", description: "Professor" },
  ];

  private _users = new BehaviorSubject<User[]>([]);
  readonly users$: Observable<User[]> = this._users.asObservable();

  constructor(private http: HttpClient) {}

  get(
    per_page: number = 10,
    page: number = 1,
    term = ""
  ): Observable<Paginate<User>> {
    return this.http.get<Paginate<User>>(
      `${environment.base_url}/users?page=${page}&per_page=${per_page}&term=${term}`
    );
  }

  update(userId: number, formData: any): Observable<any> {
    return this.http.put<any>(
      `${environment.base_url}/users/${userId}/update`,
      formData
    );
  }

  getUsersByRegisterDate(
    per_page: number = 10,
    page: number = 1,
    term = ""
  ): Observable<Paginate<User>> {
    return this.http.get<Paginate<User>>(
      `${environment.base_url}/users/get_users_by_created_at?page=${page}&per_page=${per_page}&term=${term}`
    );
  }

  getExtract(
    userId: number,
    per_page: number = 10,
    page: number = 1
  ): Observable<Paginate<UserExtract>> {
    return this.http.get<Paginate<UserExtract>>(
      `${environment.base_url}/users/${userId}/extract_admin?page=${page}&per_page=${per_page}`
    );
  }

  getPixRequest(
    userId: number,
    per_page: number = 10,
    page: number = 1
  ): Observable<Paginate<PixRequest>> {
    return this.http.get<Paginate<PixRequest>>(
      `${environment.base_url}/users/${userId}/pix_request?page=${page}&per_page=${per_page}`
    );
  }

  getPendingPix(): Observable<any> {
    return this.http.get<any>(`${environment.base_url}/users/get_pending_pix`);
  }

  payRequest(id: number, paidDate: any): Observable<any> {
    return this.http.put<any>(
      `${environment.base_url}/users/${id}/pay_request`,
      { paid_date: paidDate }
    );
  }

  getAccess(
    userId: number,
    per_page: number = 10,
    page: number = 1
  ): Observable<Paginate<UserAccess>> {
    return this.http.get<Paginate<UserAccess>>(
      `${environment.base_url}/users/${userId}/access?page=${page}&per_page=${per_page}`
    );
  }

  getCountAccess(quantity: number = 10): Observable<any> {
    return this.http.get(
      `${environment.base_url}/users/access/count?quantity=${quantity}`
    );
  }

  getPartnershipSolicitations(
    per_page: number = 10,
    page: number = 1
  ): Observable<Paginate<Institution>> {
    return this.http.get<Paginate<Institution>>(
      `${environment.base_url}/users/partnership_solicitations`
    );
  }

  acceptPartnership(institutionId: number): Observable<any> {
    return this.http.get(
      `${environment.base_url}/users/${institutionId}/accept`
    );
  }

  getGuests(param: string = ""): Observable<Guest[]> {
    return this.http.get<Guest[]>(
      `${environment.base_url}/users?param=${param}`
    );
  }

  bulkMail(form: any): Observable<any> {
    return this.http.post(`${environment.base_url}/users/bulk_mail`, form);
  }

  parseProfileName(value: string): string {
    const index = this.PROFILE_CODS.map((item) => item.id).indexOf(value);
    if (index > -1) {
      return this.PROFILE_CODS[index].description;
    }
    return "";
  }

  setPermissions(
    userId: number,
    institutionsName: Array<any>,
    isInstitution: boolean
  ): Observable<any> {
    return this.http.put<any>(
      `${environment.base_url}/users/${userId}/set_permissions`,
      { institution: institutionsName, is_institution: isInstitution }
    );
  }

  getPermissions(userId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/users/${userId}/get_permissions`
    );
  }

  deleteUser(userId: number): Observable<any> {
    return this.http.delete(`${environment.base_url}/users/${userId}/delete_user`).pipe(
      tap(() => {
        const currentUsers = this._users.getValue();
        const updatedUsers = currentUsers.filter(user => user.id !== userId);
        this._users.next(updatedUsers);
      })
    );
  }

  getRegisteredUsers(): Observable<any> {
    return this.http.get<any>(`${environment.base_url}/users/registered_users`);
  }

  updateWordsCount(userId: number, words: number): Observable<any> {
    return this.http.put<any>(
      `${environment.base_url}/users/${userId}/update_words_count`,
      { words: words }
    );
  }

  getWordsCount(userId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/users/${userId}/get_words_count`
    );
  }

  getUserWordsById(userId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/users/${userId}/get_user_words_count`
    );
  }
}
